import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { UserRole } from "@/api/types/user-management.types";
import { EmailNotVerifiedError } from "@/api/errors";

// Helper function to determine redirect path based on user role
const getRedirectPathForRole = (roles: UserRole[] | undefined): string => {
  if (!roles || roles.length === 0) return "/publisher/dashboard";

  // ALC specific roles - check more specific roles first
  if (roles.includes("ALC_BACKOFFICE_BOOK_REVIEWER")) return "/alc/book-reviewer";
  if (roles.includes("ALC_BACKOFFICE_MANAGER")) return "/alc/manager";
  if (roles.includes("ALC_BACKOFFICE_OVERVIEW")) return "/alc/overview";

  // Other roles
  if (roles.includes("ARABOOKVERSE_BACKOFFICE")) return "/arabookverse/dashboard";
  if (roles.includes("PRODUCER")) return "/producer/dashboard";
  if (roles.includes("PUBLISHER")) return "/publisher/dashboard";

  return "/publisher/dashboard";
};

interface LoginFormProps {
  onToggleForm: () => void;
  onForgotPassword?: () => void;
  redirectTo?: string;
}

export const LoginForm = ({ onToggleForm, onForgotPassword, redirectTo }: LoginFormProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { login, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Track if we're in the middle of a login attempt
  const isLoggingIn = useRef(false);

  // Navigate after successful login when user state is updated
  useEffect(() => {
    if (user && isLoggingIn.current) {
      isLoggingIn.current = false;
      const roleBasedPath = getRedirectPathForRole(user.roles);
      const targetPath = redirectTo ||
                        (location.pathname === "/publisher/uploadMetaData" ? "/publisher/uploadMetaData" : roleBasedPath);
      console.log('🔄 Navigating to:', targetPath, 'User roles:', user.roles);
      navigate(targetPath);
    }
  }, [user, navigate, location.pathname, redirectTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: t("auth.missingInformation"),
        description: t("auth.fillAllFields"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    isLoggingIn.current = true;

    try {
      await login(email, password);
      toast({
        title: t("auth.loginSuccessful"),
        description: t("auth.welcomeBack"),
      });
    } catch (error: unknown) {
      isLoggingIn.current = false;
      console.error('Login error:', error);

      if (error instanceof EmailNotVerifiedError) {
        setErrorMessage(t("auth.emailNotVerified"));
      } else {
        setErrorMessage(t("auth.invalidCredentials"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="w-full max-w-md mx-auto shadow-lg border border-gray-200" dir={isRTL ? 'rtl' : 'ltr'}>
        <CardHeader>
          <CardTitle>{t("auth.loginTitle")}</CardTitle>
          <CardDescription>
            {t("auth.loginDescription")}
          </CardDescription>
        </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className={isRTL ? 'text-right block' : ''}>{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              className={isRTL ? 'text-right' : ''}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className={isRTL ? 'text-right' : ''}>{t("auth.password")}</Label>
              {onForgotPassword && (
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-sm text-primary hover:underline"
                >
                  {t("auth.forgotPasswordLink")}
                </button>
              )}
            </div>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              className={isRTL ? 'text-right' : ''}
              showPasswordLabel={t("auth.showPassword")}
              hidePasswordLabel={t("auth.hidePassword")}
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {t("auth.loginButton")}
          </Button>

          {errorMessage && (
            <Alert variant="destructive" className="w-full">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <p className="text-sm text-muted-foreground text-center">
            {t("auth.dontHaveAccount")}{" "}
            <button
              type="button"
              onClick={onToggleForm}
              className="text-primary hover:underline font-medium"
            >
              {t("auth.registerHere")}
            </button>
          </p>
        </CardFooter>
      </form>
      </Card>
    </div>
  );
};
